// ============================================
// REGISTER PAGE - WITH TURNSTILE + i18n
// ============================================

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Anchor, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useRegister, useGoogleLogin } from '@/hooks';
import { useAuthStore } from '@/store/auth';
import { useEffect, useState, useMemo, useRef } from 'react';
import { getCountries } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/register')({
  component: RegisterPage,
});

// i18n destekli schema factory
function createRegisterSchema(t: (key: string) => string) {
  return z.object({
    firstName: z.string().min(2, t('auth:validation.firstNameMin')),
    lastName: z.string().min(2, t('auth:validation.lastNameMin')),
    email: z.string().email(t('auth:validation.invalidEmail')),
    password: z.string().min(6, t('auth:validation.passwordMin6')),
    confirmPassword: z.string(),
    nationality: z.string().min(2, t('auth:validation.selectNationality')),
    dateOfBirth: z.string().min(1, t('auth:validation.selectDateOfBirth')),
    gender: z.enum(['MALE', 'FEMALE', 'OTHER'], { errorMap: () => ({ message: t('auth:validation.selectGender') }) }),
    department: z.enum(['DECK', 'ENGINE', 'ELECTRICAL', 'CATERING', 'OTHER'], { errorMap: () => ({ message: t('auth:validation.selectDepartment') }) }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('auth:validation.passwordsMismatch'),
    path: ['confirmPassword'],
  }).and(
    z.object({
      acceptTerms: z.literal(true, {
        errorMap: () => ({ message: t('auth:validation.acceptTerms') || 'Sözleşmeleri kabul etmelisiniz.' }),
      }),
    })
  );
}

type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

function RegisterPage() {
  const { t, i18n } = useTranslation(['auth', 'enums']);
  const countries = useMemo(() => getCountries(i18n.language), [i18n.language]);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const registerMutation = useRegister();
  const googleLoginMutation = useGoogleLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleGoogleCallback = (response: any) => {
    googleLoginMutation.mutate({ credential: response.credential, role: 'SEAFARER' });
  };

  useEffect(() => {
    let checkGoogle: any;
    
    const initializeGoogleSignIn = () => {
      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '814477697426-tl8102p2a1hcqtglaikrkkvfrn5gpago.apps.googleusercontent.com',
          callback: handleGoogleCallback,
        });
        window.google.accounts.id.renderButton(
          document.getElementById('googleBtnRegister'),
          { theme: 'outline', size: 'large', width: '100%' }
        );
      }
    };

    if (window.google) {
      initializeGoogleSignIn();
    } else {
      checkGoogle = setInterval(() => {
        if (window.google) {
          initializeGoogleSignIn();
          clearInterval(checkGoogle);
        }
      }, 500);
    }

    return () => {
      if (checkGoogle) clearInterval(checkGoogle);
    };
  }, []);

  const registerSchema = useMemo(() => createRegisterSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { 
      nationality: 'TR',
      gender: undefined,
      department: undefined,
      acceptTerms: undefined,
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, navigate]);

  const honeypotRef = useRef<HTMLInputElement>(null);

  const onSubmit = (data: RegisterFormData) => {
    const { confirmPassword, ...registerData } = data;
    if (honeypotRef.current?.value) return;
    registerMutation.mutate(registerData, {
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.message || t('auth:register.errorDefault');
        if (
          message.toLowerCase().includes('email') && 
          (message.toLowerCase().includes('exist') || message.toLowerCase().includes('already registered'))
        ) {
          setError('email', { type: 'manual', message: 'Bu e-posta adresi zaten kayıtlı.' });
        } else {
          toast.error(message);
        }
      }
    });
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-8 relative"
      style={{
        backgroundImage: `url('/login-bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Mavi overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-maritime-900/85 to-maritime-700/80" />
      
      <div className="w-full max-w-xl relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <Anchor className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('auth:panelRegisterSubtitle.user')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('auth:register.title')}</h2>

          {registerMutation.isError && !errors.email && (
            <div className="mb-4 p-3 rounded-maritime bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                {(() => {
                  const err: any = registerMutation.error;
                  const msg = err?.response?.data?.message || err?.message || '';
                  return msg || t('auth:register.errorDefault');
                })()}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {/* Satır 1: Ad, Soyad */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="label">{t('auth:register.firstName')}</label>
                <input id="firstName" type="text" className={`input ${errors.firstName ? 'input-error' : ''}`} placeholder={t('auth:register.firstNamePlaceholder')} {...register('firstName')} />
                {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
              </div>
              <div>
                <label htmlFor="lastName" className="label">{t('auth:register.lastName')}</label>
                <input id="lastName" type="text" className={`input ${errors.lastName ? 'input-error' : ''}`} placeholder={t('auth:register.lastNamePlaceholder')} {...register('lastName')} />
                {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
              </div>
            </div>

            {/* Satır 2: Email, Uyruk */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="email" className="label">{t('auth:register.email')}</label>
                <input id="email" type="email" className={`input ${errors.email ? 'input-error' : ''}`} placeholder="denizci@example.com" {...register('email')} />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
              </div>
              <div>
                <label htmlFor="nationality" className="label">{t('auth:register.nationality')}</label>
                <select id="nationality" className={`input ${errors.nationality ? 'input-error' : ''}`} {...register('nationality')}>
                  <option value="">{t('auth:register.select')}</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.nationality && <p className="mt-1 text-xs text-red-500">{errors.nationality.message}</p>}
              </div>
            </div>

            {/* Satır 3: Doğum Tarihi, Cinsiyet, Departman */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="dateOfBirth" className="label">{t('auth:register.dateOfBirth')}</label>
                <input id="dateOfBirth" type="date" className={`input ${errors.dateOfBirth ? 'input-error' : ''}`} {...register('dateOfBirth')} />
                {errors.dateOfBirth && <p className="mt-1 text-xs text-red-500">{errors.dateOfBirth.message}</p>}
              </div>
              <div>
                <label htmlFor="gender" className="label">{t('auth:register.gender')}</label>
                <select id="gender" className={`input ${errors.gender ? 'input-error' : ''}`} {...register('gender')}>
                  <option value="">{t('auth:register.select')}</option>
                  <option value="MALE">{t('enums:gender.MALE')}</option>
                  <option value="FEMALE">{t('enums:gender.FEMALE')}</option>
                  <option value="OTHER">{t('enums:gender.OTHER')}</option>
                </select>
                {errors.gender && <p className="mt-1 text-xs text-red-500">{errors.gender.message}</p>}
              </div>
              <div>
                <label htmlFor="department" className="label">{t('auth:register.department')}</label>
                <select id="department" className={`input ${errors.department ? 'input-error' : ''}`} {...register('department')}>
                  <option value="">{t('auth:register.select')}</option>
                  <option value="DECK">{t('enums:department.DECK')}</option>
                  <option value="ENGINE">{t('enums:department.ENGINE')}</option>
                  <option value="ELECTRICAL">{t('enums:department.ELECTRICAL')}</option>
                  <option value="CATERING">{t('enums:department.CATERING')}</option>
                  <option value="OTHER">{t('enums:department.OTHER')}</option>
                </select>
                {errors.department && <p className="mt-1 text-xs text-red-500">{errors.department.message}</p>}
              </div>
            </div>

            {/* Satır 4: Şifre, Şifre Tekrar */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="password" className="label">{t('auth:register.password')}</label>
                <div className="relative">
                  <input id="password" type={showPassword ? 'text' : 'password'} className={`input pr-10 ${errors.password ? 'input-error' : ''}`} placeholder="••••••••" {...register('password')} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
              </div>
              <div>
                <label htmlFor="confirmPassword" className="label">{t('auth:register.confirmPassword')}</label>
                <div className="relative">
                  <input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} className={`input pr-10 ${errors.confirmPassword ? 'input-error' : ''}`} placeholder="••••••••" {...register('confirmPassword')} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
              </div>
            </div>

            
            {/* Sözleşme Onay */}
            <div className="flex items-start gap-2 mt-4">
              <input
                id="acceptTerms"
                type="checkbox"
                className={`mt-1 h-4 w-4 rounded border-gray-300 text-maritime-600 focus:ring-maritime-500 ${errors.acceptTerms ? 'border-red-500' : ''}`}
                {...register('acceptTerms')}
              />
              <label htmlFor="acceptTerms" className="text-xs text-gray-600 leading-tight">
                <a href={import.meta.env.VITE_FRONTEND_URL ? `${import.meta.env.VITE_FRONTEND_URL}/tr/terms` : 'https://crewatsea.com/tr/terms'} target="_blank" rel="noopener noreferrer" className="text-maritime-600 hover:underline">Kullanıcı Sözleşmesini</a>,{' '}
                <a href={import.meta.env.VITE_FRONTEND_URL ? `${import.meta.env.VITE_FRONTEND_URL}/tr/privacy` : 'https://crewatsea.com/tr/privacy'} target="_blank" rel="noopener noreferrer" className="text-maritime-600 hover:underline">Gizlilik Politikasını</a> ve{' '}
                <a href={import.meta.env.VITE_FRONTEND_URL ? `${import.meta.env.VITE_FRONTEND_URL}/tr/kvkk` : 'https://crewatsea.com/tr/kvkk'} target="_blank" rel="noopener noreferrer" className="text-maritime-600 hover:underline">KVKK Aydınlatma Metnini</a> okudum ve kabul ediyorum.
              </label>
            </div>
            {errors.acceptTerms && <p className="mt-1 text-xs text-red-500">{errors.acceptTerms.message}</p>}

            <button type="submit" className="btn-primary w-full mt-4" disabled={registerMutation.isPending}>
              {registerMutation.isPending ? (<><Loader2 className="w-4 h-4 animate-spin" />{t('auth:register.submitting')}</>) : t('auth:register.submit')}
            </button>
            {/* Honeypot */}
            <input
              ref={honeypotRef}
              type="text"
              name="website"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            />
          </form>

          {/* Google ile Devam Et */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-400">veya</span>
            </div>
          </div>

          <div className="w-full flex justify-center min-h-[44px]">
            {googleLoginMutation.isPending ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin text-maritime-600" />
                Giriş yapılıyor...
              </div>
            ) : (
              <>
                <style>{`
                  #googleBtnRegister {
                    display: flex !important;
                    justify-content: center !important;
                    width: 100% !important;
                  }
                  #googleBtnRegister > div {
                    margin: 0 auto !important;
                  }
                `}</style>
                <div id="googleBtnRegister" className="w-full"></div>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-gray-500">
            {t('auth:register.hasAccount')}{' '}
            <Link to="/login" className="text-maritime-600 hover:text-maritime-700 font-medium">{t('auth:register.loginLink')}</Link>
          </p>
        </div>

        <p className="text-center text-maritime-200 text-sm mt-8">{t('auth:footer.copyright', { year: new Date().getFullYear() })}</p>
      </div>
    </div>
  );
}
